import React, { useState, useEffect } from "react";
import FeesAccordion from "../../components/dashboard-components/fees-component/FeesAccordion";
import { useGetFees } from "../../sevices/hooks/admin";
import FeesModal from "../../components/dashboard-components/fees-component/FeesModal";

// Type definitions for fee data
interface FeeItem {
  id: string;
  description: string;
  type: string;
  value: {
    amount: string;
  };
  // Add other necessary properties based on API response
  [key: string]: any;
}

interface FeesData {
  status: string;
  data: FeeItem[];
}

const Fees = () => {
  const [activeAccordion, setActiveAccordion] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch fees data
  const {
    data: feesResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetFees();

  // Transform the fees data to a more usable format
  const [transformedFees, setTransformedFees] = useState<FeeItem[]>([]);

  useEffect(() => {
    if (feesResponse?.data?.data) {
      // Extract and transform the first object in the response array
      // which contains all the fee properties as demonstrated in your example data
      const feeData = feesResponse.data.data[0];

      if (feeData) {
        // Create individual fee items for each fee type in the data
        const transformedItems: FeeItem[] = [
          {
            id: "card-issuing",
            description: "Card Issuing Fee",
            type: feeData.cardIssuingFeeType,
            value: { amount: feeData.cardIssuingFee },
          },
          {
            id: "card-load",
            description: "Card Load Fee",
            type: feeData.cardLoadFeeType,
            value: { amount: feeData.cardLoadFee },
          },
          {
            id: "card-processing",
            description: "Card Processing Fee",
            type: feeData.cardProcessingFeeType,
            value: { amount: feeData.cardProcessingFee },
          },
          {
            id: "corporate-commission",
            description: "Corporate Commission",
            type: feeData.corporateCommissionType,
            value: { amount: feeData.corporateCommission },
          },
          {
            id: "cot",
            description: "COT Fee",
            type: feeData.cotType,
            value: { amount: feeData.cot },
          },
          {
            id: "crypto-conversion",
            description: "Crypto Conversion Charge",
            type: feeData.cryptoConversionChargeType,
            value: { amount: feeData.cryptoConversionCharge },
          },
          {
            id: "crypto-transfer",
            description: "Crypto Transfer Charge",
            type: feeData.cryptoTransferChargeType,
            value: { amount: feeData.cryptoTransferCharge },
          },
          {
            id: "fcy-remittance",
            description: "FCY Remittance Fee",
            type: feeData.fcyRemittanceType,
            value: { amount: feeData.fcyRemittance },
          },
          {
            id: "withdrawal",
            description: "Withdrawal Fee",
            type: feeData.withdrawalFeeType,
            value: { amount: feeData.withdrawalFee },
          },
          // Add more fee types as needed
        ];

        setTransformedFees(transformedItems);
      }
    }
  }, [feesResponse]);

  // Handle create new fee
  const handleCreateFee = () => {
    setShowCreateModal(true);
  };

  // Handle modal close and refresh data
  const handleModalClose = () => {
    setShowCreateModal(false);
    refetch(); // Refresh fees data after creating a new fee
  };

  return (
    <div className="h-full w-full bg-[#FEFEFF] p-5 md:pr-5 md:pl-8">
      <div className="flex flex-row items-center justify-between mt-8">
        <h1 className="font-bold text-2xl text-[#071827] md:pb-2">Fees</h1>

        {/* <button
          onClick={handleCreateFee}
          className="text-white border border-[#0eb683] bg-[#0eb683] px-4 py-2 rounded-md transition duration-300 active:scale-95 font-medium"
        >
          Create New Fee
        </button> */}
      </div>

      {/* Error state */}
      {isError && (
        <div className="mt-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <p>Error loading fees. Please try again later.</p>
          <button onClick={() => refetch()} className="mt-2 text-sm underline">
            Retry
          </button>
        </div>
      )}

      {/* Fee accordions */}
      <div className="mt-8">
        {isLoading
          ? // Show skeleton loaders while loading
            Array(5)
              .fill(0)
              .map((_, idx) => (
                <FeesAccordion
                  key={`skeleton-${idx}`}
                  title=""
                  value=""
                  type=""
                  id=""
                  active={activeAccordion}
                  setActive={setActiveAccordion}
                  isLoading={true}
                />
              ))
          : // Show actual fee items
            transformedFees.map((fee) => (
              <FeesAccordion
                key={fee.id}
                id={fee.id}
                title={fee.description}
                value={fee.value.amount}
                type={fee.type}
                description={fee.description}
                active={activeAccordion}
                setActive={setActiveAccordion}
              />
            ))}
      </div>

      {/* Create fee modal */}
      {showCreateModal && (
        <FeesModal isOpen={showCreateModal} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default Fees;
